import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap text-sm font-semibold ring-offset-background transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-primary-dark rounded-lg shadow-soft hover:shadow-medium hover:scale-[1.02] active:scale-[0.98]",
        destructive: "bg-destructive text-destructive-foreground hover:bg-destructive/90 rounded-lg",
        outline: "border-2 border-border bg-background hover:bg-muted hover:text-muted-foreground rounded-lg hover:scale-[1.02] active:scale-[0.98]", 
        secondary: "bg-muted text-muted-foreground hover:bg-muted/80 rounded-lg hover:scale-[1.02] active:scale-[0.98]",
        ghost: "hover:bg-muted hover:text-muted-foreground rounded-lg",
        link: "text-primary underline-offset-4 hover:underline",
        hero: "bg-gradient-primary text-white rounded-lg shadow-medium hover:shadow-strong hover:scale-[1.02] active:scale-[0.98]",
        "hero-outline": "border-2 border-white/60 bg-white/25 text-black hover:bg-white/35 hover:border-white/80 rounded-lg backdrop-blur-sm hover:scale-[1.02] active:scale-[0.98] shadow-lg hover:shadow-xl transition-all duration-200 font-semibold",
        // Sirppi Marketing Variants
        compass: "bg-compass text-compass-foreground hover:bg-compass-dark rounded-lg shadow-soft hover:shadow-medium hover:scale-[1.02] active:scale-[0.98]",
        "compass-outline": "border-2 border-compass text-compass bg-transparent hover:bg-compass/10 rounded-lg hover:scale-[1.02] active:scale-[0.98]",
        studio: "bg-studio text-studio-foreground hover:bg-studio-dark rounded-lg shadow-soft hover:shadow-medium hover:scale-[1.02] active:scale-[0.98]",
        "studio-outline": "border-2 border-studio text-studio bg-transparent hover:bg-studio/10 rounded-lg hover:scale-[1.02] active:scale-[0.98]",
      },
      size: {
        default: "h-11 px-4 py-2 min-h-[44px]",
        sm: "h-9 rounded-md px-3 min-h-[36px] text-xs",
        lg: "h-12 rounded-lg px-6 text-sm min-h-[48px]",
        xl: "h-14 rounded-lg px-8 text-base min-h-[56px]",
        icon: "h-11 w-11 rounded-lg min-h-[44px] min-w-[44px]",
        compact: "h-9 px-3 py-1.5 min-h-[36px] text-xs rounded-md",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />;
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
